import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';

import {
  CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY,
  CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY,
} from './useConvenienceDispatchDeliveries.query';
import { USE_DELIVERIES_QUERY_KEY } from './useDeliveries.query';
import { PLANNINGS_DRIVERS_QUERY_KEY } from './useQueryPlanningsDrivers';
import {
  PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY,
  USE_QUERY_PLANNINGS_DELIVERIES_QUERY_KEY,
} from './useQueryPlanningsDeliveries';

const REQUEST_PATH = '/api/v5/convenience_dispatches/shift_trucks';

type RequestBody = {
  truck_id: number;
  start_at: string;
  end_at: string;
  driver_id?: number;
  labor_hours?: number;
};

export const useConvenienceDispatchShiftTruckMutation = () => {
  const queryClient = useQueryClient();

  // shift_truck の create / destroy / update は、固定ルート画面以外の
  // 勤務計画 (Shifts) / 配車計画 (V3Plans / V3Timeline / TrunkTransportation) の
  // deliveries 表示にも影響する。これらの query は staleTime: Infinity なので、
  // 明示的に invalidate しないと別画面へ遷移しても古いキャッシュが残る。
  // また CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY は「本日なし判定」用に
  // 固定ルート選択時に保持され、AddVehicleDialog が mutation 単独で呼ぶ経路
  // (Presenter の refetch を通らない) でも cache が古くなるため必須。
  const invalidateRelatedCaches = () => {
    void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [USE_DELIVERIES_QUERY_KEY] }); // Shifts ページ
    void queryClient.invalidateQueries({ queryKey: [USE_QUERY_PLANNINGS_DELIVERIES_QUERY_KEY] }); // v3 deliveries
    void queryClient.invalidateQueries({ queryKey: [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY] }); // v5 deliveries
    // ドライバー一覧も取り直す。driver_name の手入力で新しいドライバーが増えるため、
    // ここを飛ばすと一覧に居ない id を指すことになり、名前が引けず「ドライバー未設定」に戻る
    void queryClient.invalidateQueries({ queryKey: [PLANNINGS_DRIVERS_QUERY_KEY] });
  };

  return {
    create: useMutation({
      mutationFn: (body: RequestBody) => axios.post<PlanningsDeliveryWithLegRunEntity>(REQUEST_PATH, body),

      onSuccess: (_response: AxiosResponse<PlanningsDeliveryWithLegRunEntity>) => {
        invalidateRelatedCaches();
      }
    }),
    // OFF にする操作: leg_run.operations を resetting (= 未割当に戻す) + shift_truck/shift を destroy
    destroy: useMutation({
      mutationFn: (shiftTruckId: number) => axios.delete<void>(`${REQUEST_PATH}/${shiftTruckId}`),

      onSuccess: () => {
        invalidateRelatedCaches();
      }
    }),
    // 車両 / ドライバーの差し替え (アルゴリズム再計算なし)。
    // truck_id / driver_id のいずれかまたは両方を渡す。backend で uniqueness 衝突を回避する。
    // driverName はマスタに無い人を当日だけ乗せるための口。backend が同名を探して
    // 無ければ作る。driverId を同時に渡した場合は driverId が優先される。
    update: useMutation({
      mutationFn: (args: { shiftTruckId: number; truckId?: number; driverId?: number; driverName?: string }) => axios.patch(
        `${REQUEST_PATH}/${args.shiftTruckId}`,
        { truck_id: args.truckId, driver_id: args.driverId, driver_name: args.driverName },
      ),

      onSuccess: () => {
        invalidateRelatedCaches();
      }
    }),
  };
};
